import { useCallback, useId, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  ErrorBanner,
  PageHeader,
  TextInput,
  useActionSlow,
} from '@zarya/ui';
import { t } from '@app/i18n';
import { TIMEOUT_ORDER_LONGAMS } from '@app/api/client';
import {
  useDebugPing,
  useEligibleRaw,
  useRestoreRaw,
  type EligibleRaw,
  type ExecutorRequest,
  type ExecutorResetReport,
  type RestoreRawResult,
  type SlugEligible,
} from '@app/api/hooks/debug';
import s from './Commands.module.css';

/**
 * T24 — **Comandos** (`/comandos`, 28/08).
 *
 * Página fora do menu e da busca: quem sabe a URL chega, e só admin passa (o
 * router manda os outros papéis para a Visão geral, como faz com qualquer rota
 * que não existe). O servidor recusa de qualquer forma — a rota `/debug/*` exige
 * sessão de admin —, então esconder aqui é conveniência, não controle de acesso.
 *
 * Os comandos não são fixos: a página pergunta ao backend quais existem
 * (`GET /debug/ping`) e mostra só esses. Num backend sem `POC_AUTH_DEV` nem
 * `POC_COMANDOS_OCULTOS` a página abre vazia, dizendo por quê.
 *
 * - **Zerar dados de demonstração** (`POST /debug/reset`, item 5 de 28/08) —
 *   devolve ao estado do roteiro: reaplica `demo.sql` e ressemeia o mock.
 * - **Restaurar ao estado cru** (`POST /debug/restaurar-cru`) — devolve ao estado
 *   de instalação vazia: apaga a jornada e repõe ambientes/regras de fábrica.
 */

/** Palavra que o admin digita para liberar o restauro. */
const PALAVRA = 'RESTAURAR';

export function CommandsPage() {
  const ping = useDebugPing();
  const restore = useRestoreRaw();
  // O comando apaga o domínio inteiro e pode limpar o middleware: passa de
  // segundos. `mutateAsync` (em vez de `mutate`) só para haver promessa que o
  // indicador de tarefas espere; o `catch` vazio deixa o erro no estado da
  // mutação, que já alimenta o banner.
  const wait = useActionSlow();

  const [confirmandoCru, setConfirmandoCru] = useState(false);
  const [digitado, setDigitado] = useState('');
  const fieldID = useId();

  // «Também no executor» (29/08). `marcados: null` é «ninguém mexeu» e vale
  // **todos** — o padrão é limpar tudo, porque é o que o comando existe para
  // fazer; marcar cinco caixas na mão a cada teste seria o mesmo trabalho que a
  // opção veio evitar. O primeiro clique materializa a lista.
  const [marcados, setMarcados] = useState<string[] | null>(null);
  const [erasePolicies, setApagarPoliticas] = useState(true);
  // Reset completo do executor (§13, 04/09). **Desligado por padrão**, ao
  // contrário das caixas: é a opção que apaga o catálogo do executor e a
  // numeração dos contratos, e quem a marca precisa saber que a Story 3 vai
  // ter de rodar de novo. Marcada, ela substitui as caixas — o backend recusa
  // os dois juntos.
  const [fullReset, setFullReset] = useState(false);

  // 28/08 (mrolim): «Zerar dados de demonstração» saiu da tela — reaplica seed e, nos
  // dois ambientes do lab, derrubou a configuração do tenant duas vezes. Em 08/09 a
  // decisão 5 de copy tirou o card do código: a rota `POST /debug/reset` continua
  // existindo para `make seed-demo`/E2E e para o card de Configurações do tenant;
  // esta tela oferece só «Restaurar ao estado cru», que não toca infra/tenant/usuários.
  const hasRaw = ping.data?.restaurar_cru === true;
  const nenhum = !ping.isLoading && !hasRaw;

  const eligible = useEligibleRaw(hasRaw);
  const executor = eligible.data ?? null;
  const slugsEligible = useMemo(
    () => (executor?.slugs ?? []).map((linha) => linha.slug),
    [executor],
  );
  const slugsMarcados = marcados ?? slugsEligible;
  // Só é oferecido quando o backend liberou (guard do lab inerte ou modo demo)
  // **e** há o que limpar; caso contrário o bloco aparece explicando por quê.
  const canClearExecutor = executor?.released === true && slugsEligible.length > 0;
  // O reset completo só depende de o executor estar liberado: ele não precisa
  // de slug elegível — apaga tudo lá, inclusive o que a PoC não conhece.
  const resetExecutor = executor?.released === true && fullReset;
  const clearExecutor =
    resetExecutor ||
    (!fullReset && canClearExecutor && (slugsMarcados.length > 0 || erasePolicies));
  // Uma forma **ou** outra, nunca as duas: o backend responde 422 ao par.
  const executorRequest: ExecutorRequest | undefined = !clearExecutor
    ? undefined
    : resetExecutor
      ? { reset: true }
      : { slugs: slugsMarcados, policies: erasePolicies };

  const alternarSlug = useCallback(
    (slug: string) =>
      setMarcados((current) => {
        const base = current ?? slugsEligible;
        return base.includes(slug) ? base.filter((s) => s !== slug) : [...base, slug];
      }),
    [slugsEligible],
  );

  // `useCallback` não é enfeite: o `ConfirmDialog` devolve o foco ao botão
  // «Cancelar» toda vez que `onCancel` muda de identidade. Com uma função nova a
  // cada render, cada tecla digitada no campo de confirmação roubava o foco de
  // volta — só a primeira letra entrava.
  const closeRaw = useCallback(() => {
    setConfirmandoCru(false);
    setDigitado('');
  }, []);

  return (
    <>
      <PageHeader
        title="Comandos"
        subtitle="Ferramentas de administração, fora do menu. Só para administradores, sempre com confirmação."
        icon="code"
      />

      {ping.isLoading ? <p className={s.carregando}>Carregando…</p> : null}

      {nenhum ? (
        <Card title="Nenhum comando disponível">
          <p className={s.descricao} data-testid="sem-comandos">
            Este ambiente não expõe comandos de administração.
          </p>
        </Card>
      ) : null}

      <div className={s.cards}>
        {hasRaw ? (
          <Card
            title="Restaurar ao estado cru"
            subtitle="Volta ao estado de um tenant recém-criado."
          >
            <section aria-label="Restaurar ao estado cru" data-testid="comando-cru">
              <p className={s.descricao}>
                {t('common.rawReset.description')}
              </p>
              <ul className={s.efeitos}>
                <li>{t('common.rawReset.defaults')}</li>
                <li>
                  {t('common.rawReset.preserved')}
                </li>
                <li>
                  {resetExecutor ? (
                    <>
                      <strong>Reset completo do middleware.</strong> Apaga no middleware
                      identidades, contratos, catálogo, regras e módulos Rego; esquece as decisões
                      e esvazia o admission e o PDP. Só a configuração do middleware fica.{' '}
                      <strong>Importe o catálogo de novo</strong> antes de aprovar qualquer release.
                    </>
                  ) : clearExecutor ? (
                    <>
                      <strong>Também limpa o middleware.</strong> Revoga os contratos marcados abaixo
                      {erasePolicies ? ' e esvazia as regras do PDP' : ''}. O catálogo{' '}
                      <strong>não</strong> é tocado.
                    </>
                  ) : (
                    <>
                      <strong>Não toca no middleware.</strong> Os contratos que já estão lá
                      continuam: a identidade aparece como «descoberta · sem contrato» até a
                      próxima aprovação.
                    </>
                  )}
                </li>
                <li>Também esquece as posições arrastadas no Mapa de acesso.</li>
              </ul>

              <ExecutorBlock
                elegiveis={eligible.isLoading ? undefined : executor}
                marcados={slugsMarcados}
                apagarPoliticas={erasePolicies}
                fullReset={fullReset}
                onAlternarSlug={alternarSlug}
                onAlternarPoliticas={() => setApagarPoliticas((v) => !v)}
                onAlternarFullReset={() => setFullReset((v) => !v)}
              />

              {restore.isSuccess ? <RestoreResult resultado={restore.data} /> : null}
              {restore.error ? (
                <ErrorBanner
                  error={restore.error}
                  escrita
                  title="Não foi possível restaurar"
                  hideLink
                />
              ) : null}

              <Button
                variant="danger"
                onClick={() => setConfirmandoCru(true)}
                disabled={restore.isPending}
              >
                Restaurar ao estado cru
              </Button>
              <ConfirmDialog
                open={confirmandoCru}
                title="Restaurar o tenant ao estado cru?"
                destructive
                confirmLabel="Restaurar"
                loading={restore.isPending}
                // A confirmação digitada é o que separa este comando dos outros
                // botões destrutivos da aplicação: aqui não há uma entidade em
                // foco para o impacto nomear — apaga-se a jornada inteira.
                confirmDisabled={digitado.trim().toUpperCase() !== PALAVRA}
                message={
                  <>
                    <p>
                      {t('common.rawReset.confirmation')}
                      {clearExecutor ? '' : ' O middleware não é tocado.'}
                    </p>
                    {resetExecutor ? (
                      <div data-testid="confirmacao-executor">
                        <p>
                          <strong>No middleware:</strong> reset completo; só a configuração dele
                          fica. O catálogo também sai: <strong>importe-o de novo</strong> antes de
                          aprovar. As decisões das últimas 24 h somem da tela Decisões.
                        </p>
                        <p>
                          Os pods continuam rodando; um restart é recusado pelo admission até a
                          nova aprovação.
                        </p>
                      </div>
                    ) : clearExecutor ? (
                      <div data-testid="confirmacao-executor">
                        <p>
                          <strong>No middleware:</strong>{' '}
                          {slugsMarcados.length > 0
                            ? `revoga o contrato ativo de ${slugsMarcados.join(', ')}`
                            : 'nenhum contrato'}
                          {erasePolicies ? ' e esvazia as regras do PDP' : ''}. O catálogo não é
                          tocado.
                        </p>
                        <p>
                          Os pods continuam rodando; um restart é recusado pelo admission até a
                          nova aprovação.
                        </p>
                      </div>
                    ) : null}
                    <div className={s.confirmacao}>
                      <label htmlFor={fieldID}>
                        Digite <strong>{PALAVRA}</strong> para liberar:
                      </label>
                      <TextInput
                        id={fieldID}
                        mono
                        autoComplete="off"
                        value={digitado}
                        onChange={(e) => setDigitado(e.target.value)}
                      />
                    </div>
                  </>
                }
                onConfirm={() =>
                  wait
                    .executar(() => restore.mutateAsync(executorRequest), {
                      titulo: 'Restaurar ao estado cru',
                      // Mesmo teto que `useRestaurarCru` deu ao axios.
                      limiteMs: TIMEOUT_ORDER_LONGAMS,
                    })
                    .catch(() => {})
                    .finally(closeRaw)
                }
                onCancel={closeRaw}
              />
            </section>
          </Card>
        ) : null}
      </div>
    </>
  );
}

/**
 * «Também no executor» (29/08) — as caixas do que será revogado lá.
 *
 * A lista vem do backend (`GET /debug/restaurar-cru/elegiveis`): as três regras
 * que decidem quem é elegível — allowlist do guard do lab, agente conhecido da
 * PoC ou do executor, nunca MCP — dependem de dados que a tela não tem, e um
 * filtro repetido aqui divergiria do que o `POST` aceita.
 *
 * `undefined` é «ainda carregando»; `null` é «a rota não existe» (backend mais
 * antigo), e aí o bloco não aparece: o comando continua sendo o de 28/08.
 */
function ExecutorBlock({
  elegiveis,
  marcados,
  apagarPoliticas,
  fullReset,
  onAlternarSlug,
  onAlternarPoliticas,
  onAlternarFullReset,
}: {
  elegiveis: EligibleRaw | null | undefined;
  marcados: string[];
  apagarPoliticas: boolean;
  fullReset: boolean;
  onAlternarSlug: (slug: string) => void;
  onAlternarPoliticas: () => void;
  onAlternarFullReset: () => void;
}) {
  if (elegiveis === undefined) {
    return <p className={s.carregando}>Consultando o middleware…</p>;
  }
  if (elegiveis === null) {
    return null;
  }

  return (
    <fieldset className={s.executor} data-testid="bloco-executor" disabled={!elegiveis.released}>
      <legend className={s.legenda}>Também no middleware</legend>

      {elegiveis.released ? (
        <>
          <p className={s.descricao}>
            Revoga o contrato ativo de cada identidade marcada. O middleware atualiza o admission e
            o PDP; o catálogo <strong>não</strong> é tocado.
          </p>
          {/*
            Reset completo (§13, 04/09): a alternativa às caixas. Marcada, ela as
            desabilita — o backend recusa `reset` junto de `slugs`/`policies`, e
            uma tela que deixasse marcar os dois mandaria um pedido que sabe ser
            inválido.
          */}
          <label className={s.politicas} data-testid="executor-reset">
            <input type="checkbox" checked={fullReset} onChange={onAlternarFullReset} />
            <span>Reset completo do middleware</span>
          </label>
          <p className={s.descricao}>
            Em vez de revogar identidade a identidade: apaga no middleware identidades, contratos,{' '}
            <strong>catálogo</strong>, regras e módulos Rego; esquece as decisões e esvazia o
            admission e o PDP. Só a configuração do middleware fica.
          </p>
        </>
      ) : (
        <p className={s.descricao} data-testid="executor-bloqueado">
          Limpar o middleware é uma ação global e só está disponível em ambientes de demonstração
          {elegiveis.executor_url
            ? ` (middleware: ${elegiveis.executor_url})`
            : ', com um middleware configurado no tenant'}
          .
        </p>
      )}

      {elegiveis.error_code ? (
        <p className={s.descricao} data-testid="executor-nao-lido">
          O estado no middleware não foi lido (<code>{elegiveis.error_code}</code>). A lista abaixo
          é o que o produto conhece; a revogação continua valendo.
        </p>
      ) : null}

      {elegiveis.slugs.length === 0 ? (
        <p className={s.descricao} data-testid="executor-sem-slugs">
          Nenhuma identidade elegível: MCP servers e identidades fora da lista permitida não
          entram.
        </p>
      ) : (
        <ul className={s.caixas}>
          {elegiveis.slugs.map((linha) => (
            <li key={linha.slug}>
              <label>
                <input
                  type="checkbox"
                  checked={marcados.includes(linha.slug)}
                  disabled={fullReset}
                  onChange={() => onAlternarSlug(linha.slug)}
                />
                <span className={s.nome}>{linha.slug}</span>
                <span className={s.estado} data-testid={`estado-${linha.slug}`}>
                  {stateInExecutor(linha)}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      <label className={s.politicas}>
        <input
          type="checkbox"
          checked={apagarPoliticas}
          disabled={fullReset}
          onChange={onAlternarPoliticas}
        />
        <span>Apagar as regras no middleware (PDP)</span>
      </label>
    </fieldset>
  );
}

/**
 * O relatório do reset completo (§13), como o executor o devolveu: linhas por
 * tabela **dele**, contadores reiniciados, módulos removidos, decisões
 * esquecidas. Sem tradução dos nomes — são do executor, e traduzi-los aqui
 * seria a segunda fonte da mesma verdade.
 */
function ExecutorResetSummary({ relatorio }: { relatorio: ExecutorResetReport }) {
  const linhas = Object.entries(relatorio.deleted).sort(
    ([a, na], [b, nb]) => nb - na || a.localeCompare(b),
  );
  return (
    <div className={s.passos} data-testid="relatorio-executor">
      <h4 className={s.subtitulo}>Apagado no middleware</h4>
      <ul>
        {linhas.map(([tabela, n]) => (
          <li key={tabela} data-testid={`executor-apagadas-${tabela}`}>
            <span className={s.nome}>{tabela}</span> — {n}
          </li>
        ))}
        <li>
          Contadores reiniciados:{' '}
          {relatorio.sequences_reset.length > 0 ? relatorio.sequences_reset.join(', ') : 'nenhum'}
        </li>
        <li>
          Módulos Rego removidos do PDP:{' '}
          {relatorio.pdp_modules_removed.length > 0
            ? relatorio.pdp_modules_removed.join(', ')
            : 'nenhum'}
        </li>
        <li>Decisões esquecidas pelo coletor: {relatorio.collector_cleared}</li>
        <li>Preservado no middleware: {relatorio.preserved.join(', ')}</li>
      </ul>
    </div>
  );
}

/**
 * O estado da identidade no executor, em três leituras — e a terceira é a que
 * importa: `has_contract: null` é «não lido», nunca «sem contrato». Afirmar que
 * não há contrato sobre um executor que ninguém leu é a conclusão que este
 * produto não tira em lugar nenhum (AC-38).
 */
function stateInExecutor(linha: SlugEligible): string {
  if (linha.has_contract === null) {
    return 'estado não lido';
  }
  if (!linha.has_contract) {
    return linha.in_executor ? 'sem contrato' : 'ainda não replicada no middleware';
  }
  return linha.contract === null ? 'contrato ativo' : `contrato #${linha.contract} ativo`;
}

/**
 * O reset completo foi pedido a um middleware que **não o tem** — versão anterior
 * ao §13 (`rota_ausente`) ou chave `AIG_RESET_RAW` desligada no deploy
 * (`reset_raw_disabled`). Não é falha de execução, é ausência, e a tela diz isso
 * em vez de um erro genérico (exigência do lead, 04/09): o banco local foi
 * restaurado, o executor não foi tocado, e a limpeza slug a slug continua lá.
 */
const CODES_RESET_UNAVAILABLE = ['rota_ausente', 'reset_raw_disabled'] as const;

function resetUnavailable(resultado: RestoreRawResult): string | null {
  // `steps` é «nunca nulo» no contrato de 29/08 — mas um backend de 28/08 não o
  // manda, e a tela continua abrindo o resultado dele.
  const step = (resultado.steps ?? []).find(
    (s) =>
      s.action === 'executor.reset' &&
      !s.ok &&
      (CODES_RESET_UNAVAILABLE as readonly string[]).includes(s.code ?? ''),
  );
  return step?.code ?? null;
}

/** Os passos da limpeza no executor, um por linha, com o código quando houve. */
function ExecutorSteps({ resultado }: { resultado: RestoreRawResult }) {
  if (!resultado.executor_touched) {
    return null;
  }
  return (
    <div className={s.passos} data-testid="passos-executor">
      <h4 className={s.subtitulo}>No middleware</h4>
      <ul>
        {resultado.steps.map((step) => (
          <li key={`${step.target}:${step.action}`} data-testid={`passo-${step.target}`}>
            <span className={step.ok ? s.passoOk : s.passoErro}>{step.ok ? '✓' : '✕'}</span>{' '}
            <span className={s.nome}>{step.target}</span> — {step.detail}
            {step.code ? (
              <>
                {' '}
                (<code>{step.code}</code>)
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** O que sumiu, por tabela, mais o que ficou de pé. */
function RestoreResult({ resultado }: { resultado: RestoreRawResult }) {
  const linhas = Object.entries(resultado.deleted).sort(
    ([a, na], [b, nb]) => nb - na || a.localeCompare(b),
  );
  const total = linhas.reduce((soma, [, n]) => soma + n, 0);
  const unavailable = resetUnavailable(resultado);

  return (
    <div className={s.resultado} data-testid="resultado-cru">
      <Alert tone="info" title="Tenant restaurado ao estado cru">
        <span data-testid="aviso-restaurado">
          {total === 0
            ? 'Não havia nada da jornada para apagar.'
            : `${total} ${total === 1 ? 'linha apagada' : 'linhas apagadas'}.`}{' '}
          Preservados: {resultado.preserved.join(', ')}.
          {resultado.executor_reset
            ? ' O middleware foi reiniciado por completo; só a configuração dele ficou. Importe o catálogo de novo antes de aprovar. Os pods continuam rodando, mas um restart é recusado pelo admission até a nova aprovação.'
            : unavailable
              ? ' O middleware não foi tocado: o reset completo não está disponível nesta versão (veja abaixo).'
              : resultado.executor_touched
                ? ' O middleware também foi limpo: os pods continuam rodando, mas um restart é recusado pelo admission até a nova aprovação.'
                : resultado.executor_url
                  ? ` O middleware (${resultado.executor_url}) não foi tocado; a identidade aparece como «descoberta · sem contrato» até nova aprovação.`
                  : ' O middleware não foi tocado.'}
        </span>
      </Alert>

      {unavailable ? (
        <Alert tone="warning" title="Reset completo indisponível neste middleware">
          <span data-testid="executor-reset-indisponivel">
            O middleware respondeu <code>{unavailable}</code>:{' '}
            {unavailable === 'rota_ausente'
              ? 'esta versão do middleware não tem o reset completo.'
              : 'o reset completo está desligado no middleware.'}{' '}
            O banco local foi restaurado; o middleware não foi tocado. Use a limpeza identidade a
            identidade (as caixas acima) ou{' '}
            {unavailable === 'rota_ausente' ? 'atualize o middleware' : 'habilite o reset no middleware'}{' '}
            e repita.
          </span>
        </Alert>
      ) : null}

      <ExecutorSteps resultado={resultado} />
      {resultado.executor_reset ? <ExecutorResetSummary relatorio={resultado.executor_reset} /> : null}

      <div className={s.rolagem}>
        <table className={s.tabela}>
          <caption className="sr-only">Linhas apagadas por tabela</caption>
          <thead>
            <tr>
              <th scope="col">Tabela</th>
              <th scope="col">Linhas apagadas</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map(([table, n]) => (
              <tr key={table} data-testid={`apagadas-${table}`}>
                <td className={s.nome}>{table}</td>
                <td className={n === 0 ? s.zero : undefined}>{n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resultado.cleaned.length > 0 ? (
        <p className={s.descricao}>Também limpo: {resultado.cleaned.join('; ')}.</p>
      ) : null}
    </div>
  );
}
